// v3.0.0-rc.7-phaseCD-CD_WALLET_RICH_PURCHASE_V1 — Phase C.2 · WalletTopupModal
//
// Modale de rechargement Mobile Money (mock CinetPay). 4 providers : Orange/MTN/Moov/Wave.
// Endpoint cible : POST /api/v3/wallet/topup (confirmé via grep walletB2cRoute.js).
// Body : { provider, amount_fcfa, phone }
// Réponses :
//   success → { ok:true, status:'completed', new_balance_fcfa, ... }
//   failure → { ok:false, status:'failed', error_code:'PROVIDER_TIMEOUT', error_fr:'…' }
//   validation → 400/403 { ok:false, error, detail }
//
// Palette héritée de WalletB2cPage (vert émeraude + accent gold).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "@/utils/apiClient";
// [H_UIPOLISH_V1_L3 Palier 2.A] Polish-only imports.
import { AyaConfettiBurst } from "@/components/ui/ConfettiBurst";
import { useAyaToast } from "@/components/ui/Toast";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const C = {
  emeraldDeep: "#0F2E20",
  emeraldMid: "#1B5436",
  emeraldLight: "#2A7350",
  gold: "#D4AF37",
  ivory: "#F5F1E6",
  ivoryDim: "rgba(245, 241, 230, 0.72)",
  ivoryFade: "rgba(245, 241, 230, 0.42)",
  surfaceLine: "rgba(212, 175, 55, 0.22)",
  errorBg: "rgba(244, 67, 54, 0.16)",
  errorFg: "#ffb4ab",
  successBg: "rgba(46, 160, 110, 0.18)",
  successFg: "#9bd9b1",
};

const PROVIDERS = [
  { id: "orange", label: "Orange Money", short: "Orange", accent: "#FF6F00" },
  { id: "mtn", label: "MTN Mobile Money", short: "MTN", accent: "#FFC107" },
  { id: "moov", label: "Moov Money", short: "Moov", accent: "#2E78D2" },
  { id: "wave", label: "Wave", short: "Wave", accent: "#1DC4F2" },
];

const AMOUNT_SUGGESTIONS = [1000, 2500, 5000, 10000, 25000, 50000];

const MIN_FCFA = 500;
const MAX_FCFA = 500000;

function _normalizePhone(raw) {
  const cleaned = String(raw || "").replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+225")) return cleaned;
  if (cleaned.startsWith("225")) return "+" + cleaned;
  if (/^\d{10}$/.test(cleaned)) return "+225" + cleaned;
  return cleaned;
}

export default function WalletTopupModal({ open, onClose, defaultPhone = "", onSuccess }) {
  const [provider, setProvider] = useState("orange");
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState(defaultPhone || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // [H_UIPOLISH_V1_L3 Palier 2.A] Polish-only refs/hooks.
  const confettiRef = useRef(null);
  const toast = useAyaToast();

  useEffect(() => {
    if (open) {
      setProvider("orange");
      setAmount("");
      setPhone(defaultPhone || "");
      setError(null);
      setSuccess(null);
      setSubmitting(false);
    }
  }, [open, defaultPhone]);

  const numAmount = useMemo(() => Number(String(amount).replace(/\D/g, "")), [amount]);
  const phoneNormalized = useMemo(() => _normalizePhone(phone), [phone]);
  const phoneValid = /^\+225\d{10}$/.test(phoneNormalized);
  const amountValid = Number.isFinite(numAmount) && numAmount >= MIN_FCFA && numAmount <= MAX_FCFA;
  const canSubmit = !submitting && !!provider && amountValid && phoneValid && !success;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await apiFetch(`${BACKEND}/api/v3/wallet/topup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, amount_fcfa: numAmount, phone: phoneNormalized }),
      });
      const data = await r.json().catch(() => null);
      if (r.ok && data?.ok && data?.status === "completed") {
        setSuccess(data);
        // [H_UIPOLISH_V1_L3 Palier 2.A] Polish-only side-effects (visuel) :
        // burst confetti + toast success. Aucun changement de logique métier :
        // setSuccess + setTimeout(onSuccess, 1200) restent inchangés.
        try { confettiRef.current?.fire({ origin: { x: 0.5, y: 0.4 } }); } catch (_e) { /* noop */ }
        try {
          toast.success({
            title: "Recharge réussie",
            description: `Nouveau solde : ${new Intl.NumberFormat("fr-FR").format(Number(data.new_balance_fcfa) || 0)} FCFA`,
          });
        } catch (_e) { /* noop */ }
        // Auto-close + propage la nouvelle balance après 1.2s.
        setTimeout(() => onSuccess?.(Number(data.new_balance_fcfa) || null), 1200);
        return;
      }
      // Échec provider (mock 5%) : data.ok=false, status='failed'.
      if (data?.status === "failed" && data?.error_fr) {
        setError(data.error_fr);
      } else if (data?.detail) {
        setError(data.detail);
      } else if (data?.error) {
        setError(`Erreur : ${data.error}`);
      } else {
        setError(`Erreur réseau (HTTP ${r.status}). Réessaie.`);
      }
    } catch (e) {
      setError(e?.message || "Erreur réseau.");
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, provider, numAmount, phoneNormalized, onSuccess, toast]);

  if (!open) return null;

  return (
    <div
      data-testid="wallet-topup-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0, 0, 0, 0.6)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        padding: "0 12px 0",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: "100%", maxWidth: 460,
          background: `linear-gradient(160deg, ${C.emeraldDeep} 0%, ${C.emeraldMid} 100%)`,
          borderTopLeftRadius: 24, borderTopRightRadius: 24,
          border: `1px solid ${C.surfaceLine}`, borderBottom: "none",
          color: C.ivory, padding: "18px 18px 24px",
          boxShadow: "0 -10px 40px rgba(0,0,0,0.5)",
          maxHeight: "92vh", overflowY: "auto",
        }}
      >
        <div style={{ width: 44, height: 4, background: C.surfaceLine, borderRadius: 2, margin: "0 auto 14px" }} />

        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.ivory }}>Recharger mon portefeuille</h3>
          <button
            data-testid="wallet-topup-close-btn"
            onClick={() => { if (!submitting) onClose?.(); }}
            disabled={submitting}
            style={{ background: "transparent", border: "none", color: C.ivoryFade, fontSize: 22, cursor: submitting ? "not-allowed" : "pointer", padding: "0 4px" }}
            aria-label="Fermer"
          >×</button>
        </div>
        <p style={{ margin: "0 0 16px", fontSize: 12, color: C.ivoryDim }}>
          Choisis ton opérateur Mobile Money et le montant à créditer.
        </p>

        {/* Providers */}
        <div data-testid="wallet-topup-providers" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          {PROVIDERS.map((p) => {
            const active = provider === p.id;
            return (
              <button
                key={p.id}
                data-testid={`wallet-topup-provider-${p.id}`}
                onClick={() => setProvider(p.id)}
                disabled={submitting || !!success}
                style={{
                  padding: "12px 10px",
                  borderRadius: 12,
                  border: active ? `1.5px solid ${C.gold}` : `1px solid ${C.surfaceLine}`,
                  background: active ? "rgba(212, 175, 55, 0.12)" : "rgba(15,46,32,0.6)",
                  color: C.ivory,
                  fontSize: 13, fontWeight: 600,
                  cursor: submitting || success ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: 8, textAlign: "left",
                }}
              >
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: p.accent, flexShrink: 0 }} />
                {p.short}
              </button>
            );
          })}
        </div>

        {/* Amount */}
        <label style={{ fontSize: 12, color: C.ivoryDim, display: "block", marginBottom: 6 }}>Montant (FCFA)</label>
        <input
          data-testid="wallet-topup-amount-input"
          type="text"
          inputMode="numeric"
          value={amount}
          placeholder="Ex : 5000"
          onChange={(e) => setAmount(e.target.value.replace(/\D/g, "").slice(0, 7))}
          disabled={submitting || !!success}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "12px 14px", borderRadius: 12,
            background: "rgba(15,46,32,0.8)",
            border: `1px solid ${amountValid || !amount ? C.surfaceLine : C.errorFg}`,
            color: C.ivory, fontSize: 16, fontWeight: 600,
            outline: "none",
          }}
        />
        <div data-testid="wallet-topup-amount-suggestions" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, marginBottom: 14 }}>
          {AMOUNT_SUGGESTIONS.map((v) => (
            <button
              key={v}
              data-testid={`wallet-topup-amount-suggest-${v}`}
              onClick={() => setAmount(String(v))}
              disabled={submitting || !!success}
              style={{
                padding: "6px 12px", borderRadius: 999,
                background: numAmount === v ? C.gold : "transparent",
                color: numAmount === v ? C.emeraldDeep : C.ivoryDim,
                border: `1px solid ${numAmount === v ? C.gold : C.surfaceLine}`,
                fontSize: 12, fontWeight: 600,
                cursor: submitting || success ? "not-allowed" : "pointer",
              }}
            >{new Intl.NumberFormat("fr-FR").format(v)}</button>
          ))}
        </div>

        {/* Phone */}
        <label style={{ fontSize: 12, color: C.ivoryDim, display: "block", marginBottom: 6 }}>
          Numéro Mobile Money <span style={{ color: C.ivoryFade }}>(format +225XXXXXXXXXX)</span>
        </label>
        <input
          data-testid="wallet-topup-phone-input"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+225 07 00 00 00 00"
          disabled={submitting || !!success}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "12px 14px", borderRadius: 12,
            background: "rgba(15,46,32,0.8)",
            border: `1px solid ${phoneValid || !phone ? C.surfaceLine : C.errorFg}`,
            color: C.ivory, fontSize: 14,
            outline: "none",
          }}
        />

        {/* Error / Success */}
        {error && (
          <div data-testid="wallet-topup-error" style={{ marginTop: 14, background: C.errorBg, color: C.errorFg, padding: "10px 14px", borderRadius: 12, fontSize: 13 }}>
            {error}
          </div>
        )}
        {success && (
          <div data-testid="wallet-topup-success" style={{ marginTop: 14, background: C.successBg, color: C.successFg, padding: "12px 14px", borderRadius: 12, fontSize: 13 }}>
            ✓ Recharge réussie. Nouveau solde : {new Intl.NumberFormat("fr-FR").format(Number(success.new_balance_fcfa) || 0)} FCFA.
          </div>
        )}

        {/* Submit */}
        <button
          data-testid="wallet-topup-submit-btn"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            width: "100%", marginTop: 18,
            padding: "14px 18px",
            background: canSubmit ? C.gold : "rgba(212, 175, 55, 0.35)",
            color: C.emeraldDeep,
            border: "none", borderRadius: 14,
            fontSize: 15, fontWeight: 700,
            letterSpacing: 0.3,
            cursor: canSubmit ? "pointer" : "not-allowed",
            boxShadow: canSubmit ? "0 6px 16px rgba(212, 175, 55, 0.34)" : "none",
          }}
        >
          {submitting ? "Traitement en cours…" : success ? "Recharge confirmée" : "Confirmer la recharge"}
        </button>
        <p style={{ marginTop: 10, fontSize: 11, color: C.ivoryFade, textAlign: "center", lineHeight: 1.4 }}>
          Mock CinetPay · Latence simulée 1,5 – 2,5s. Plafond {new Intl.NumberFormat("fr-FR").format(MAX_FCFA)} FCFA / recharge.
        </p>
        {/* [H_UIPOLISH_V1_L3 Palier 2.A] Confetti controller (invisible) */}
        <AyaConfettiBurst ref={confettiRef} palette="emerald-gold" particleCount={80} />
      </div>
    </div>
  );
}
